import { useEffect, useState, useRef } from "react";
import { deleteMessageAttachment, getThread, getThreadUsers, updateMessage, updateThreadSubject, uploadAttachment, type Message, type Thread } from "../../services/MessageService";
import type { UserDTO } from "../../services/AuthService";
import { useAuth } from "../../security/AuthContext";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useNotification } from "../../components/Notifications";
import { Avatar } from "../../components/Avatar";
import { Tooltip } from "../../components/Tooltip";
import { MenuItem } from "../../components/MenuItem";
import { Menu } from "../../components/Menu";
import { CircleButton } from "../../components/CircleButton";
import { Hide } from "../../components/Hide";
import { Dialog } from "../../components/Dialog";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { Show } from "../../components/Show";
import { AttachmentsEdit } from "./AttachmentsEdit";
import { Recipients } from "./list/Recipients";

// Helper function to format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) {
    console.warn('Timestamp is missing or invalid:', timestamp);
    return 'Unknown';
  }

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date parsed from timestamp:', timestamp);
      return 'Unknown';
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error parsing timestamp:', timestamp, error);
    return 'Unknown';
  }
};

export const MessageView = () => {
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [thread, setThread] = useState<Thread>({
    id: 0,
    createdId: 0,
    createdTs: null,
    recipients: [],
    subject: '',
    ts: null,
    messages: [],
    messagesCount: 0
  });

  const [users, setUsers] = useState(new Map());
  const [message, setMessage] = useState<Message>({
    id: 0,
    recipients: []
  } as Message);

  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [editRows, setEditRows] = useState<number>(3);
  const [dropdownOpenId, setDropdownOpenId] = useState<number | null>(null);
  const [isReplyExpanded, setIsReplyExpanded] = useState<boolean>(false);
  const [replyError, setReplyError] = useState('');
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const headerDropdownRef = useRef(null);
  const [attachmentDeleteId, setAttachmentDeleteId] = useState(0);

  useEffect(() => {
    setMouseHandlers();
    loadThread();
  }, []);

  useEffect(() => {
    const usersMap = new Map(thread.recipients.map(user => [user.id, user]));
    setUsers(usersMap);
  }, [thread]);

  const setMouseHandlers = () => {
    const handleClickOutside = (event) => {
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
        setIsEditingSubject(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  };

  const loadThread = () => {
    getThread(Number(id))
      .then(data => {
        console.log('Thread data:', data);
        setThread(data);
      })
      .catch(err => {
        addNotification(`Error loading message ${id} ${err.message}`, 'error');
      });
  };

  const onReplyKeyDown = (e) => {
    if (e.target.value) {
      setReplyError('');
    }
  }

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    var msg = message || {} as Message;
    msg.id = 0;
    msg.createdId = user.id;
    msg.createdTs = new Date();
    msg.threadId = thread.id;
    msg.draft = "N";
    msg.content = e.target.querySelector('textarea').value;
    msg.ts = new Date();

    if (!msg.content) {
      setReplyError('Please provide content');
      return;
    }

    try {
      const persisted = await updateMessage(msg);
      addNotification("Message added", "success");

      for (const att of attachments) {
        try {
          const res = await uploadAttachment(persisted.id, att);
          addNotification(`Attachment ${att.name} uploaded`, "success");
        } catch (err) {
          addNotification(`Failed to upload attachment ${att.name} : ` + err.message, "error");
        }
      }
      loadThread();
      e.target.reset();
      setAttachments([]);
      setIsReplyExpanded(false);
    } catch (err) {
      addNotification("Error adding message", "error");
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditMessage = (e, message: Message) => {
    e.stopPropagation();
    setEditingMessageId(message.id);
    setEditContent(message.content);
    const lineCount = message.content.split('\n').length;
    setEditRows(Math.min(Math.max(lineCount, 3), 10));
    setDropdownOpenId(null);
  };

  const handleEditSubmit = async (messageId: number) => {
    const updatedMessage: Message = {
      ...thread.messages.find(m => m.id === messageId),
      content: editContent,
      ts: new Date(),
    };
    try {
      await updateMessage(updatedMessage);
      addNotification("Message updated", "success");
      setEditingMessageId(null);
      setEditContent('');
      setEditRows(3);
      loadThread();
    } catch (err) {
      addNotification("Error updating message", "error");
    }
  };

  const handleEditCancel = (messageId: number) => {
    setEditingMessageId(null);
    setEditContent('');
    setEditRows(3);
  };

  const handleDelete = (messageId: number) => {
    addNotification('Delete functionality not implemented', 'warning');
    setDropdownOpenId(null);
  };

  const handleDeleteThread = () => {
    addNotification('Delete thread functionality not implemented', 'warning');
  };

  const handleEditThread = (e) => {
    e.stopPropagation();
    setIsEditingSubject(true);
  };

  const handleInvite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigate('/messages/participants/' + thread.id);
  };

  const handleSaveEditSubject = async (value) => {
    if (value.trim() === '') {
      addNotification('Subject cannot be empty', 'error');
      return;
    }
    try {
      await updateThreadSubject(thread.id, value);
      addNotification('Subject updated successfully', 'success');
      setThread((prev) => ({ ...prev, subject: value }));
    } catch (err) {
      addNotification('Error updating thread ' + err.message, 'error');
    }
    setIsEditingSubject(false);
  };

  const handleCancelEditSubject = (e) => {
    e.stopPropagation();
    setIsEditingSubject(false);
  };

  const getUserName = (userId: number): string => {
    let user = thread.recipients.filter((d: UserDTO) => d.id == userId)[0];
    return user
      ? user.lastName && user.firstName ? user.lastName + ' ' + user.firstName : user.email
      : 'UNKNOWN';
  };

  const isMessageEdited = (message: Message): boolean => {
    return new Date(message.ts).getTime() > new Date(message.createdTs).getTime();
  };

  const attachmentUrl = (id) => {
    return `/api/attachment/${id}?token=` + localStorage.getItem('token');
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const onAttachmentDelete = async (id) => {
    try {
      await deleteMessageAttachment(id);
      addNotification('Attachment deleted', 'success')
      loadThread();
    } catch (err) {
      addNotification('Error deleting attachment ' + err.message, 'error')
    }
  }

  const SubjectEditor = ({visible, subject, onSubmit, onCancel}) => {
    const [editedSubject, setEditedSubject] = useState(subject)
    const subjectInputRef = useRef(null);

    useEffect(() => {
      setTimeout(() => {
        subjectInputRef.current?.focus();
      });
    }, []);

    const handleSubjectInputKeyDown = async (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSave(null);
      }
    };

    const onSave = (e) => {
      onSubmit(subjectInputRef.current.value)
    }; 

    return (visible ?
      <div className="flex items-center gap-2 w-6/7">
        <input
          ref={subjectInputRef}
          type="text"
          value={editedSubject}
          onChange={(e) => setEditedSubject(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleSubjectInputKeyDown}
          placeholder="Enter subject"
          className="flex-1 text-xl font-bold text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Edit thread subject"
        />
        <CircleButton 
          clickHandler={onSave} 
          icon="fa-check"
          tooltip="Save Subject" 
        />
        <CircleButton 
          clickHandler={onCancel} 
          icon="fa-times"
          tooltip="Cancel Edit" 
        />
      </div> 
      : <></>     
    );
  };

  const SubjectMenu = () => {
    return (
      <Menu>
        <MenuItem 
          key={id} 
          clickHandler={handleInvite} 
          icon='fa-user-plus text-indigo-500' 
          text='Invite' 
          tooltip="Invite To Thread"
        />
        {thread.createdId == user.id && 
          <>
            <MenuItem
              clickHandler={handleEditThread}
              icon={'fa-pencil text-indigo-500'}
              text='Edit'
              tooltip="Edit Thread" 
            />
            <MenuItem
              clickHandler={handleDeleteThread}
              icon='fa-trash text-red-800'
              text='Delete'
              textClass="text-red-800"
              tooltip="Delete Thread" 
            />
          </>  
        }
      </Menu>
    );
  };


  return (
    <div className="mx-auto bg-gray-100 min-h-screen">
      {/* Subject view and editor */}
      <div className="bg-white p-4 mb-4 rounded-lg shadow-sm relative">
        <div className="flex justify-between mb-2">
          <SubjectEditor 
            visible={isEditingSubject}
            subject={thread.subject} 
            onSubmit={handleSaveEditSubject} 
            onCancel={handleCancelEditSubject} 
          /> 
          <Hide hide={isEditingSubject}>
            <div>
              <h1 className="text-l font-bold text-gray-700">{thread.subject || "Email Thread"}</h1>
              <p className="text-sm text-gray-400">
                {thread.messages.length} messages, created {formatTime(thread.createdTs)}
              </p>
              <div className="pt-1">
                <Recipients recipients={thread.recipients}/>
              </div>
            </div>
            <SubjectMenu />
          </Hide>          
        </div>
      </div>
      {/* Email Messages */}
      <div className="space-y-4 mb-6">
        {thread.messages.map((message) => (
          <div key={message.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Avatar user={users.get(message.createdId)} />
                <div>
                  <span className="text-sm font-semibold text-gray-600">{getUserName(message.createdId)}</span>
                  <span className="text-sm text-gray-400 ml-2">{formatTime(message.createdTs)}</span>
                </div>
              </div>
              {message.createdId === user.id && (
                <div className="relative">
                  {editingMessageId === message.id 
                  ? 
                    <div className="flex items-center gap-2">
                      <CircleButton
                        clickHandler={() => handleEditSubmit(message.id)}
                        icon="fa-check"
                        tooltip="Confirm Edit" 
                        disableAfterClick={true}
                      />
                      <CircleButton
                        clickHandler={() => handleEditCancel(message.id)}
                        icon="fa-xmark"
                        tooltip="Cancel Edit" 
                      />
                    </div>
                  : 
                    <Menu>
                        <MenuItem 
                          clickHandler={(e) => handleEditMessage(e, message)} 
                          icon="fa-pencil text-indigo-500"
                          text="Edit"
                          tooltip="Edit Message"
                        />
                        <MenuItem
                          clickHandler={() => handleDelete(message.id)}
                          icon="fa-trash text-red-800"
                          text="Delete"
                          textClass="text-red-800"
                          tooltip="Delete Message"
                        />   
                    </Menu>
                  }
                </div>
              )}
            </div>
            {editingMessageId === message.id ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleEditSubmit(message.id);
                }}
              >
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
                  rows={editRows}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  aria-label="Edit message content"
                  autoFocus
                />
              </form>
            ) : (
              <div>
                <p className="text-gray-700 whitespace-pre-wrap mb-3">{message.content}</p>
                {isMessageEdited(message) && (
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Edited {formatTime(thread.ts)}</span>
                  </div>
                )}
              </div>
            )}

            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2">
                <hr className="h-px my-1 bg-gray-200 border-0 dark:bg-gray-300"></hr>
                <ul className="list-inside text-sm text-blue-600">
                  {message.attachments.map((attachment, index) => (
                    <li key={index}>
                      <a
                        href={attachmentUrl(attachment.id)}
                        className="hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {attachment.name}
                      </a>
                      <span className="px-1 text-gray-500">{formatFileSize(attachment.size)}</span>
                      <button 
                        className="text-gray-400 p-2 rounded hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 cursor-pointer"
                        onClick={() => {setAttachmentDeleteId(attachment.id)}}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                      <ConfirmationDialog
                        open={attachmentDeleteId == attachment.id}
                        setOpen={(openFlag) => setAttachmentDeleteId(openFlag ? attachment.id : 0)}
                        title='Delete attachment confirmation'
                        text={`Do you want to delete attachment ${attachment.name}?`}
                        onConfirm={() => onAttachmentDelete(attachment.id)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Reply Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Reply</h2>
        <form onSubmit={handleReplySubmit}>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
            rows={6}
            placeholder="Type your reply here..."
            aria-label="Reply to thread"
            onClick={() => setIsReplyExpanded(true)}
            onKeyDown={onReplyKeyDown}
          ></textarea>
          <Show show={replyError}>
            <p className="text-sm text-red-600 mb-3">{replyError}</p>
          </Show>

          {isReplyExpanded && (
            <div>
              <AttachmentsEdit 
                attachments={attachments}
                setAttachments={setAttachments}
              />

              <div className="flex justify-end gap-4">
                <div className="relative group">
                  <button
                    type="submit"
                    className="flex-1 max-w-[120px] w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                    aria-label="Send reply"
                  >
                    Send Reply
                  </button>
                </div>
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setIsReplyExpanded(false)}
                    className="flex-1 max-w-[120px] w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    aria-label="Collapse reply section"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};